import { useEffect } from 'react';
import type { MutableRefObject } from 'react';
import type { InspectionTemplate, TemplateSection } from 'inspection-types';

type Params = {
  template: InspectionTemplate | undefined;
  sortedSections: TemplateSection[];
  mainColumnRef: MutableRefObject<HTMLDivElement | null>;
  setActiveSectionId: (sectionId: string) => void;
};

export function useInspectionTemplateCustomizeSectionObserver({
  template,
  sortedSections,
  mainColumnRef,
  setActiveSectionId,
}: Params): void {
  useEffect(() => {
    const root = mainColumnRef.current;

    if (!template || sortedSections.length === 0 || !root) {
      return undefined;
    }

    const elements = sortedSections
      .map(s => document.getElementById(`section-${s.id}`))
      .filter((el): el is HTMLElement => el !== null);

    if (elements.length === 0) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      entries => {
        const intersecting = entries
          .filter(e => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);

        if (intersecting[0]?.target.id?.startsWith('section-')) {
          const id = intersecting[0].target.id.replace('section-', '');

          setActiveSectionId(id);
        }
      },
      {
        root,
        rootMargin: '-10% 0px -55% 0px',
        threshold: [0, 0.1, 0.25, 0.5, 1],
      }
    );

    elements.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, [template, sortedSections, mainColumnRef, setActiveSectionId]);
}
